import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { insertMissingQuizzes } from '@/scripts/insertMissingQuizzes';
import { orchestrationService } from '@/services/orchestrationService';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export function AutoQuizInsertion() {
  const { toast } = useToast();
  const [status, setStatus] = useState<'idle' | 'running' | 'complete' | 'error'>('idle');
  const [insertResult, setInsertResult] = useState<any>(null);
  const [orchestrationState, setOrchestrationState] = useState<any>(null);
  const [quizCount, setQuizCount] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);

  const executeInsertion = async () => {
    setStatus('running');
    setProgress(10);

    try {
      // Step 1: Insert quizzes
      toast({
        title: '🚀 Auto-Executing Quiz Insertion',
        description: 'Inserting 15 missing quizzes...',
      });

      setProgress(20);
      const result = await insertMissingQuizzes(false);
      setInsertResult(result);
      setProgress(50);

      toast({
        title: `✅ Inserted ${result.success.length} Quizzes`,
        description: `Success: ${result.success.length}, Failed: ${result.failed.length}, Skipped: ${result.skipped.length}`,
      });

      // Step 2: Get updated quiz count
      const { count } = await supabase
        .from('module_quizzes')
        .select('*', { count: 'exact', head: true });
      
      setQuizCount(count);
      setProgress(70);

      // Step 3: Trigger orchestrator
      toast({
        title: '🎯 Triggering Orchestrator',
        description: 'Verifying Phase 3 completion...',
      });

      await orchestrationService.triggerOrchestrator();
      setProgress(85);

      // Step 4: Wait and check final state
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const finalState = await orchestrationService.getState();
      setOrchestrationState(finalState);
      setProgress(100);

      setStatus('complete');

      toast({
        title: '✅ Automation Complete',
        description: `Quizzes: ${count}, Phase 3: ${finalState?.phase3_practice_complete ? 'Complete' : 'In Progress'}`,
      });

    } catch (error: any) {
      console.error('Auto-insertion failed:', error);
      setStatus('error');
      toast({
        title: '❌ Automation Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    // Auto-run on mount
    executeInsertion();
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {status === 'running' && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
          {status === 'complete' && <CheckCircle className="h-5 w-5 text-green-600" />}
          {status === 'error' && <XCircle className="h-5 w-5 text-red-600" />}
          Automated Quiz Insertion
        </CardTitle>
        <CardDescription>
          {status === 'idle' && 'Initializing...'}
          {status === 'running' && 'Running automated insertion process...'}
          {status === 'complete' && 'Automation completed successfully'}
          {status === 'error' && 'Automation encountered errors'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} />
        </div>

        {/* Results Grid */}
        {insertResult && (
          <div className="grid grid-cols-3 gap-4">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <div>
                <p className="font-medium">{insertResult.success.length}</p>
                <p className="text-muted-foreground">Success</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <XCircle className="h-4 w-4 text-red-600" />
              <div>
                <p className="font-medium">{insertResult.failed.length}</p>
                <p className="text-muted-foreground">Failed</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <div>
                <p className="font-medium">{insertResult.skipped.length}</p>
                <p className="text-muted-foreground">Skipped</p>
              </div>
            </div>
          </div>
        )}

        {/* Database Stats */}
        {quizCount !== null && (
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <span className="text-sm font-medium">Total Quizzes in Database</span>
            <Badge variant="outline" className="text-lg">{quizCount}</Badge>
          </div>
        )}

        {/* Orchestration Status */}
        {orchestrationState && (
          <div className="space-y-2 p-3 rounded-lg bg-muted/50">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Orchestration Phase</span>
              <Badge>{orchestrationState.current_phase}</Badge>
            </div>
            <div className="grid grid-cols-4 gap-2 text-xs">
              <Badge variant={orchestrationState.phase1_content_complete ? 'default' : 'outline'}>
                Phase 1
              </Badge>
              <Badge variant={orchestrationState.phase2_quizzes_complete ? 'default' : 'outline'}>
                Phase 2
              </Badge>
              <Badge variant={orchestrationState.phase3_practice_complete ? 'default' : 'outline'}>
                Phase 3
              </Badge>
              <Badge variant={orchestrationState.phase4_assessments_complete ? 'default' : 'outline'}>
                Phase 4
              </Badge>
            </div>
          </div>
        )}

        {/* Failed Items Details */}
        {insertResult && insertResult.failed.length > 0 && (
          <div className="p-3 rounded-lg border border-destructive/20 bg-destructive/5">
            <p className="text-sm font-medium mb-2">Failed Insertions:</p>
            <ul className="text-xs space-y-1 text-muted-foreground">
              {insertResult.failed.map((f: any) => (
                <li key={f.moduleId}>• {f.moduleId}: {f.error}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
